import { CustomerService, IApplicationFilter, PortfolioReportCache, WidgetPropertyType, enumToExtendedArray, getChartReadyPromise, property, widget } from '@dynamik/common';
import { color, create, percent, RoundedRectangle, unuseTheme, useTheme } from '@amcharts/amcharts4/core';
import { PieChart, PieSeries, XYChart, ColumnSeries, CategoryAxis, ValueAxis, Legend } from '@amcharts/amcharts4/charts';
import { InstrumentValueGroup } from '@dynamik/common/clients/reporting-clients';
import { BindingEngine, observable } from 'aurelia-binding';
import { ChartWidgetBase } from '@dynamik/common';
import { customElement } from 'aurelia-templating';
import { Size } from '@aurelia-mdc-web/base';
import { Mandate, MandatesClient } from '@dynamik/common/clients/transaction-clients';

export enum PortfolioOverviewChartType {
  Pie = 1,
  Bar = 2
}

export enum PortfolioOverviewChartGroupBy {
  Type = 2,
  Category = 3
}

declare module '@dynamik/common/clients/reporting-clients' {
  interface InstrumentValueGroup {
    colour?: string;
    percentage?: number;
    mandate?: number;
  }
}

@customElement('portfolio-overview-chart-new-widget')
@widget({ name: 'Portfolio overview chart (new)', canUseInReport: true, canUseInDashboard: true, description: 'Description', defaultWidth: 62, defaultHeight: 53 })
export class PortfolioOverviewChartNewWidget extends ChartWidgetBase {
  filter: IApplicationFilter;
  constructor(element: Element, customerService: CustomerService, bindingEngine: BindingEngine, private portfolioReportCache: PortfolioReportCache,
    private mandatesClient: MandatesClient) {
    super(element, bindingEngine, customerService);
  }

  chartObject: PieChart | XYChart;
  data: InstrumentValueGroup[];
  showMandates: boolean;

  @property('Data', 'Type', WidgetPropertyType.List, enumToExtendedArray(PortfolioOverviewChartType))
  chartType: PortfolioOverviewChartType = PortfolioOverviewChartType.Pie;

  @property('Data')
  radius?: number = 80;

  @property('Data')
  innerRadius?: number = 32;

  @property('Data', undefined, WidgetPropertyType.List, enumToExtendedArray(PortfolioOverviewChartGroupBy))
  groupBy: PortfolioOverviewChartGroupBy = PortfolioOverviewChartGroupBy.Type;

  @property('Data', 'Category type', WidgetPropertyType.List)
  categoryTypeId: number | undefined;
  async categoryTypeIdListValues() {
    return this.customerService.instrumentCategories.filter(x => x.enabled).map(x => ({ value: x.catTypeId, label: x.catTypeName }));
  }

  @property('Style')
  chartTheme: string;

  @property('Style')
  labelColour?: string;

  @observable
  size: Size;
  sizeChanged() {
    if (!this.chartObject?.legend) {
      return;
    }
    this.chartObject.legend.position = this.size.width < 600 ? 'right' : 'bottom';
  }

  override async loadData(filter: IApplicationFilter) {
    this.filter = filter
    return ({
      values: await this.portfolioReportCache.instrumentValueGroupsOverTime(filter.customer.companyId, this.groupBy === PortfolioOverviewChartGroupBy.Category ? this.categoryTypeId : undefined,
        // we're only interested in the latest date for this widget
        filter.dates.toStr, filter.dates.toStr, false),
      mandates: await this.mandatesClient.get(filter.customer.companyId)
    });
  }

  override async setData(data: { values: InstrumentValueGroup[], mandates: Mandate[] }) {
    this.chartObject?.dispose();
    switch (this.chartType) {
      case PortfolioOverviewChartType.Pie:
        await this.createPieChart();
        break;
      case PortfolioOverviewChartType.Bar:
        await this.createColumnChart();
        break;
    }

    let filteredData = data.values.filter(x => x.date >= this.filter.dates.toStr).sort((a, b) => b.estimatedValue - a.estimatedValue);
    if (filteredData.length > 5) {
      const topFour = filteredData.slice(0, 4);
      const remaining = filteredData.slice(4);

      // Aggregate the remaining items into an "Other" category
      const otherCategory = new InstrumentValueGroup({
        group: "Other",
        date: this.filter.dates.toStr,
        estimatedValue: remaining.reduce((sum, item) => sum + item.estimatedValue, 0),
        costPrice: remaining.reduce((sum, item) => sum + item.costPrice, 0),
        realizedProfitLoss: remaining.reduce((sum, item) => sum + item.realizedProfitLoss, 0),
        unrealizedProfitLoss: remaining.reduce((sum, item) => sum + item.unrealizedProfitLoss, 0),
        profitLossYearToDate: remaining.reduce((sum, item) => sum + item.profitLossYearToDate, 0),
        interest: remaining.reduce((sum, item) => sum + item.interest, 0),
        dividend: remaining.reduce((sum, item) => sum + item.dividend, 0)
      });

      filteredData = [...topFour, otherCategory];
    }

    this.chartObject.data = this.data = filteredData;
    const totalValue = this.data.reduce((acc, x) => acc + x.estimatedValue, 0);
    this.data.forEach((x, i) => {
      x.colour = this.chartObject.colors.getIndex(i).toString();
      x.percentage = totalValue > 0 ? (x.estimatedValue / totalValue) * 100 : 0;
    });
    this.showMandates = false;
    if (this.groupBy === PortfolioOverviewChartGroupBy.Type && data.mandates?.[0]?.instrumentType !== undefined) {
      this.data.forEach(x => x.mandate = data.mandates.find(m => m.instrumentType === x.groupId)?.percentage ?? 0);
      this.showMandates = true;
    }

    if (this.groupBy === PortfolioOverviewChartGroupBy.Category && data.mandates?.[0]?.category?.categoryTypeId === this.categoryTypeId && data.mandates?.[0]?.categoryId !== undefined) {
      this.data.forEach(x => {
        if (x.group !== 'Other') {
          x.mandate = data.mandates.find(m => (m.categoryId ?? null) === x.groupId)?.percentage ?? 0;
        } else {
          x.mandate = data.mandates.filter(m => !filteredData.find(fd => fd.groupId === m.categoryId)).reduce((sum, item) => sum + item.percentage, 0);
        }
       });
       this.showMandates = true;
     }
     this.excelData = {
       fileName: 'Portfolio Overview Chart',
       data: { Items: this.data }
     };
   }

  async createPieChart() {
    const theme = this.customerService.getChartTheme(this.chartTheme);
    useTheme(theme);
    this.chartObject?.dispose();
    this.chartObject = create(this.chart, PieChart);
    const readyPromise = getChartReadyPromise(this.chartObject);
    this.chartObject.radius = percent(this.radius);
    this.chartObject.innerRadius = percent(this.innerRadius);
    this.chartObject.hiddenState.properties.opacity = 0;
    const pieSeries = this.chartObject.series.push(new PieSeries());
    pieSeries.dataFields.value = "estimatedValue";
    pieSeries.dataFields.category = "group";
    pieSeries.slices.template.cornerRadius = 0;
    pieSeries.slices.template.strokeWidth = 0;
    pieSeries.slices.template.strokeOpacity = 0;
    pieSeries.numberFormatter.numberFormat = '#,###.';
    pieSeries.labels.template.disabled = true;
    pieSeries.tooltip.autoTextColor = false
    pieSeries.tooltip.label.fill = color("#f2eded");
    pieSeries.slices.template.tooltipHTML = `<b>{category}:</B>  {value.percent}%<br>
                          <b>Estimated Value:</b>  {estimatedValue}<br>
                          <b>Unrealized Profit/Loss:</b>  {unrealizedProfitLoss}<br>
                          <b>Realized Profit/Loss:</b>  {realizedProfitLoss}<br>
                          <b>Interest:</b>  {interest}<br>
                          <b>Dividend:</b>  {dividend}`;
    this.chartObject.responsive.enabled = true;

    const labelColour = color(this.getChartLabelColour(this.labelColour));
    this.chartObject.legend = new Legend();
    this.chartObject.legend.position = this.size.width < 600 ? 'right' : 'bottom';
    this.chartObject.legend.align = 'center';
    this.chartObject.legend.valign = 'middle';
    this.chartObject.legend.contentAlign = 'center';
    this.chartObject.legend.labels.template.fill = labelColour;
    this.chartObject.legend.labels.template.fontSize = 12;
    this.chartObject.legend.itemContainers.template.valign = 'middle';
    this.chartObject.legend.labels.template.valign = 'middle';
    this.chartObject.legend.labels.template.wrap = true;
    this.chartObject.legend.markers.template.valign = 'middle';
    this.chartObject.legend.valueLabels.template.disabled = true;
    this.chartObject.legend.markers.template.width = 8;
    this.chartObject.legend.markers.template.height = 8;
    const marker = this.chartObject.legend.markers.template.children.getIndex(0) as any as RoundedRectangle;
    marker.cornerRadius(4, 4, 4, 4);
    unuseTheme(theme);
    await readyPromise;
  }

  async createColumnChart() {
    const theme = this.customerService.getChartTheme(this.chartTheme);
    useTheme(theme);
    this.chartObject?.dispose();
    this.chartObject = create(this.chart, XYChart);
    const readyPromise = getChartReadyPromise(this.chartObject);
    this.chartObject.height = percent(100);
    this.chartObject.hiddenState.properties.opacity = 0;
    this.chartObject.fontSize = 9;

    const categoryAxis = this.chartObject.xAxes.push(new CategoryAxis());
    categoryAxis.dataFields.category = 'category';
    categoryAxis.renderer.labels.template.wrap = true;
    categoryAxis.renderer.labels.template.rotation = -90;
    const labelColour = color(this.getChartLabelColour(this.labelColour));
    categoryAxis.renderer.labels.template.fill = labelColour;
    categoryAxis.renderer.minGridDistance = 30;
    categoryAxis.renderer.grid.template.location = 0.5;
    categoryAxis.renderer.labels.template.horizontalCenter = 'right';
    categoryAxis.renderer.labels.template.verticalCenter = 'middle';

    const yAxis = new ValueAxis();
    yAxis.numberFormatter.numberFormat = '#a';
    this.chartObject.yAxes.push(yAxis);
    yAxis.renderer.labels.template.fill = labelColour;

    const colSeries = this.chartObject.series.push(new ColumnSeries());
    colSeries.dataFields.valueY = 'value';
    colSeries.dataFields.categoryX = 'category';

    colSeries.columns.template.stroke = color('#343a40');
    colSeries.columns.template.strokeWidth = 2;
    colSeries.columns.template.strokeOpacity = 1;

    colSeries.numberFormatter.numberFormat = '#,###.';
    colSeries.columns.template.tooltipText = '{category} {valueY}';
    this.chartObject.responsive.enabled = true;
    // Add distinctive colors for each column using adapter
    colSeries.columns.template.adapter.add('fill', (fill, target) => {
      return this.chartObject.colors.getIndex(target.dataItem.index);
    });
    unuseTheme(theme);
    await readyPromise;
  }

  override configChanged(key: keyof PortfolioOverviewChartNewWidget) {
    switch (key) {
      case 'radius':
        (this.chartObject as PieChart).radius = percent(this.radius);
        break;
      case 'innerRadius':
        (this.chartObject as PieChart).innerRadius = percent(this.innerRadius);
        break;
      case 'labelColour':
        const labelColour = color(this.getChartLabelColour(this.labelColour));
        if (this.chartObject instanceof XYChart) {
          this.chartObject.xAxes.each(x => x.renderer.labels.template.fill = x.renderer.ticks.template.stroke = labelColour);
          this.chartObject.yAxes.each(x => x.renderer.labels.template.fill = x.renderer.ticks.template.stroke = labelColour);
        } else {
          this.chartObject.series.each(x => x.labels.template.fill = x.ticks.template.stroke = labelColour);
        }
        break;
      default:
        this.refresh$.next();
        break;
    }
  }

}
